import React, { useState } from 'react';
import {
  CButton,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalTitle,
  CModalFooter,
  CModalHeader,
  CFormGroup,
} from '@coreui/react';
import _t from 'counterpart';
import { findErrorFromValidation, handleFormError } from '../../helpers';
import { cloneGroup } from '../../services/BackendService';
import { useMutation } from 'react-query';
import { IGroup } from './types';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import toast from 'react-hot-toast';

interface ICloneGroupProps {
  show: boolean;
  hideCloneGroup: (refetch?: boolean) => void;
  groupToClone: IGroup | null;
}

const CloneGroupModal = ({ show, hideCloneGroup, groupToClone }: ICloneGroupProps) => {
  const [newGroupName, setNewGroupName] = useState<string>('');

  const findError = findErrorFromValidation;

  const cloneGroupMutation = useMutation(
    ['clone-group'],
    () => {
      if (!groupToClone?.id) return Promise.reject(new Error('No group to clone'));

      return cloneGroup(groupToClone.id, newGroupName);
    },
    {
      onSuccess: () => {
        toast.success(_t('groups.group-successfully-cloned'));
        setNewGroupName('');
        hideCloneGroup(true);
      },
      onError: (error: any) => {
        handleFormError(error);
      },
    }
  );

  const handleCloneGroup = () => {
    if (!newGroupName.trim()) {
      toast.error(_t('groups.group-name-required'));
      return;
    }
    if (!groupToClone?.id) {
      toast.error('Group data not loaded yet');
      return;
    }
    cloneGroupMutation.mutate();
  };

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setNewGroupName(target.value);
  };

  const hideSelf = () => {
    cloneGroupMutation.reset();
    setNewGroupName('');
    hideCloneGroup();
  };

  const cloneGroupErrors = cloneGroupMutation.error;

  return (
    <CModal show={show} onClose={hideSelf} closeOnBackdrop={false}>
      <CModalHeader>
        <CModalTitle>{_t('groups.clone-group')}</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CFormGroup>
          <CLabel htmlFor="name">{_t('groups.name')}</CLabel>
          <CInput
            type="text"
            name="name"
            placeholder="New group name"
            value={newGroupName}
            onChange={handleInputChange}
          />
          {findError(cloneGroupErrors, 'name') && (
            <CLabel className="text-danger">{findError(cloneGroupErrors, 'name')}</CLabel>
          )}
        </CFormGroup>
        {groupToClone && (
          <div className="text-muted">
            <small>
              {_t('groups.cloning-from')}: <strong>{groupToClone.name}</strong>
            </small>
          </div>
        )}
      </CModalBody>
      <CModalFooter>
        <ButtonWithLoader
          isLoading={cloneGroupMutation.isLoading}
          onClick={handleCloneGroup}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('groups.clone')}
          className="mr-2"
          disabled={!groupToClone?.id}
        />
        <CButton color="light" variant="outline" onClick={hideSelf} disabled={cloneGroupMutation.isLoading}>
          {_t('action.cancel')}
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default CloneGroupModal;
